// RateScope engine — solo consultant rate database.

export interface RateEntry {
  id: string;
  skill: string;
  seniority: 'junior' | 'mid' | 'senior' | 'principal' | 'expert';
  location: string;
  rateUSD: number;          // hourly USD
  engagementType: 'hourly' | 'daily' | 'project' | 'retainer';
  source: 'personal' | 'community' | 'public';
  date: string;
  note?: string;
}

// Seed benchmark data (community + public reports, anonymised) — buyer can extend.
export const SEED_RATES: Omit<RateEntry, 'id'>[] = [
  { skill: 'Business Analyst', seniority: 'senior', location: 'US Remote', rateUSD: 110, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'mid', location: 'US Remote', rateUSD: 75, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'principal', location: 'US Remote', rateUSD: 165, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Product Manager', seniority: 'senior', location: 'US Remote', rateUSD: 135, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Product Manager', seniority: 'mid', location: 'US Remote', rateUSD: 95, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'UX Designer', seniority: 'senior', location: 'US Remote', rateUSD: 120, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Full-stack Developer', seniority: 'senior', location: 'US Remote', rateUSD: 130, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Full-stack Developer', seniority: 'principal', location: 'US Remote', rateUSD: 180, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Data Engineer', seniority: 'senior', location: 'US Remote', rateUSD: 145, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'KYC/AML Consultant', seniority: 'senior', location: 'US Remote', rateUSD: 175, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'KYC/AML Consultant', seniority: 'principal', location: 'US Remote', rateUSD: 225, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Compliance Officer', seniority: 'senior', location: 'US Remote', rateUSD: 150, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Project Manager', seniority: 'senior', location: 'US Remote', rateUSD: 115, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Scrum Master', seniority: 'senior', location: 'US Remote', rateUSD: 100, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'senior', location: 'Canada', rateUSD: 90, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'senior', location: 'UK', rateUSD: 95, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'senior', location: 'EU', rateUSD: 85, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Business Analyst', seniority: 'senior', location: 'India', rateUSD: 35, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
  { skill: 'Full-stack Developer', seniority: 'senior', location: 'EU', rateUSD: 100, engagementType: 'hourly', source: 'community', date: '2026-01-15' },
];

export function bandFor(entries: RateEntry[], skill: string, seniority: string, location: string): { count: number; p25: number; median: number; p75: number } | null {
  const matches = entries.filter(e => e.skill === skill && e.seniority === seniority && e.location === location);
  if (matches.length === 0) return null;
  const rates = matches.map(e => e.rateUSD).sort((a, b) => a - b);
  const pct = (p: number) => rates[Math.min(rates.length - 1, Math.floor(rates.length * p))];
  return { count: rates.length, p25: pct(0.25), median: pct(0.5), p75: pct(0.75) };
}

export function uniqueValues<K extends keyof RateEntry>(entries: RateEntry[], key: K): RateEntry[K][] {
  return Array.from(new Set(entries.map(e => e[key]))).sort() as RateEntry[K][];
}

export function calculateQuote(hours: number, hourlyRate: number, discountPercent: number = 0): { gross: number; discount: number; net: number; effective: number } {
  const gross = hours * hourlyRate;
  const discount = gross * (discountPercent / 100);
  const net = gross - discount;
  return { gross, discount, net, effective: hours > 0 ? net / hours : 0 };
}

export function importCSV(csv: string): RateEntry[] {
  const lines = csv.trim().split('\n').filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
  const idx = (k: string) => headers.indexOf(k);
  return lines.slice(1).map(line => {
    const cols = line.split(',').map(c => c.trim().replace(/"/g, ''));
    return {
      id: crypto.randomUUID(),
      skill: cols[idx('skill')] || '',
      seniority: (cols[idx('seniority')] || 'mid') as RateEntry['seniority'],
      location: cols[idx('location')] || '',
      rateUSD: parseFloat(cols[idx('rateusd')] || cols[idx('rate')] || '0') || 0,
      engagementType: (cols[idx('engagementtype')] || cols[idx('type')] || 'hourly') as RateEntry['engagementType'],
      source: (cols[idx('source')] || 'personal') as RateEntry['source'],
      date: cols[idx('date')] || new Date().toISOString().slice(0, 10),
      note: cols[idx('note')] || undefined,
    };
  }).filter(e => e.skill && e.rateUSD > 0);
}

export function exportCSV(entries: RateEntry[]): string {
  const headers = ['Skill','Seniority','Location','RateUSD','EngagementType','Source','Date','Note'];
  const rows = entries.map(e => [e.skill, e.seniority, e.location, e.rateUSD, e.engagementType, e.source, e.date, e.note || '']);
  return [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
}
